// Your play history, per tune (Oct 2026, the discovery plan's `tuneHistory`):
// how many times you opened each tune, how long the band played it, and when.
// The home page's Most played shelf reads it, and Top picks weighs what you
// play. Local first: persisted and shared between tabs, and kept in your
// account (migration 0018) one row per device — each device writes only its
// own history and adds the others' up (`combineHistories`), so two devices
// never write the same counter. Pure: the store and the sync call these.
//
// Entries are keyed by the tune's identity (`tuneIdentityOf`, the key band
// memory and playback records use), which holds while you transpose or edit;
// the ref is how the tune was last opened, so a shelf can open it again.

import { parsePlaylistTuneRef, playlistRefKey, type PlaylistTuneRef } from '@/storage/playlists';

export interface TuneHistoryEntry {
  /** How to open the tune again: as it was last opened. */
  ref?: PlaylistTuneRef;
  title: string;
  composer?: string;
  opens: number;
  /** Seconds the band played it. */
  playSeconds: number;
  /** Epoch milliseconds. */
  lastOpened?: number;
  lastPlayed?: number;
}

export type TuneHistory = Record<string, TuneHistoryEntry>;

/** Tunes kept: the most recently active, so the record cannot grow without bound. */
export const HISTORY_LIMIT = 300;
/** Recent tunes kept: a dozen covers a practice week without turning into a library. */
export const RECENT_LIMIT = 12;
/** Played at least this long, a tune counts as played (the plan's two minutes). */
export const PLAYED_SECONDS = 120;
/** How many tunes the home page's Most played shelf shows, and the fewest it
 * shows at all: one or two played tunes are no ranking, only the Recent tunes
 * shelf again. */
export const MOST_PLAYED = 6;
export const MOST_PLAYED_MIN = 3;

const lastActive = (entry: TuneHistoryEntry) =>
  Math.max(entry.lastOpened ?? 0, entry.lastPlayed ?? 0);

/** The most recently active HISTORY_LIMIT tunes. */
export function pruneHistory(history: TuneHistory): TuneHistory {
  return prune(history);
}

function prune(history: TuneHistory): TuneHistory {
  const keys = Object.keys(history);
  if (keys.length <= HISTORY_LIMIT) return history;
  const kept = keys
    .sort((a, b) => lastActive(history[b]) - lastActive(history[a]) || a.localeCompare(b))
    .slice(0, HISTORY_LIMIT);
  return Object.fromEntries(kept.map((key) => [key, history[key]]));
}

/** A tune was opened. */
export function recordOpen(
  history: TuneHistory,
  key: string,
  ref: PlaylistTuneRef,
  at: number,
): TuneHistory {
  const entry = history[key];
  return prune({
    ...history,
    [key]: {
      ...entry,
      ref,
      title: ref.title,
      ...(ref.composer ? { composer: ref.composer } : {}),
      opens: (entry?.opens ?? 0) + 1,
      playSeconds: entry?.playSeconds ?? 0,
      lastOpened: at,
    },
  });
}

/** The band played a tune for `seconds`. A tune never opened by reference
 * (a blank or imported one) is kept by its title; a shelf skips it. */
export function recordPlay(
  history: TuneHistory,
  key: string,
  tune: { title: string; composer?: string; ref?: PlaylistTuneRef | null },
  seconds: number,
  at: number,
): TuneHistory {
  if (!(seconds > 0) || !Number.isFinite(seconds)) return history;
  const entry = history[key];
  const ref = entry?.ref ?? tune.ref ?? undefined;
  return prune({
    ...history,
    [key]: {
      ...entry,
      opens: entry?.opens ?? 0,
      ...(ref ? { ref } : {}),
      title: entry?.title ?? tune.title,
      ...(entry?.composer || tune.composer ? { composer: entry?.composer ?? tune.composer } : {}),
      playSeconds: (entry?.playSeconds ?? 0) + seconds,
      lastPlayed: at,
    },
  });
}

const count = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;

/** A persisted or another tab's history, guarded field by field: an entry
 * without a title or with no counts is dropped, a bad ref or date is left out. */
export function normalizeTuneHistory(raw: unknown): TuneHistory {
  const out: TuneHistory = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!key || !value || typeof value !== 'object') continue;
    const entry = value as Record<string, unknown>;
    const title = typeof entry.title === 'string' && entry.title.trim() ? entry.title : null;
    const opens = count(entry.opens);
    const playSeconds = count(entry.playSeconds);
    if (!title || opens === null || playSeconds === null) continue;
    const ref = parsePlaylistTuneRef(entry.ref);
    const composer = typeof entry.composer === 'string' && entry.composer ? entry.composer : null;
    const lastOpened = count(entry.lastOpened);
    const lastPlayed = count(entry.lastPlayed);
    out[key] = {
      ...(ref ? { ref } : {}),
      title,
      ...(composer ? { composer } : {}),
      opens: Math.round(opens),
      playSeconds,
      ...(lastOpened !== null ? { lastOpened } : {}),
      ...(lastPlayed !== null ? { lastPlayed } : {}),
    };
  }
  return prune(out);
}

/** Your devices' histories added up: opens and play time summed, the latest
 * times kept, and each tune named and opened as it was most recently. */
export function combineHistories(...histories: TuneHistory[]): TuneHistory {
  const out: TuneHistory = {};
  for (const history of histories) {
    for (const [key, entry] of Object.entries(history)) {
      const had = out[key];
      if (!had) {
        out[key] = { ...entry };
        continue;
      }
      const latest = lastActive(entry) > lastActive(had) ? entry : had;
      out[key] = combined(latest, had, entry, (a, b) => a + b);
    }
  }
  return out;
}

/** One tune's two entries as one: named by the latest, counts by `count`. */
function combined(
  latest: TuneHistoryEntry,
  a: TuneHistoryEntry,
  b: TuneHistoryEntry,
  count: (a: number, b: number) => number,
): TuneHistoryEntry {
  const lastOpened = Math.max(a.lastOpened ?? 0, b.lastOpened ?? 0);
  const lastPlayed = Math.max(a.lastPlayed ?? 0, b.lastPlayed ?? 0);
  const ref = latest.ref ?? a.ref ?? b.ref;
  const composer = latest.composer ?? a.composer ?? b.composer;
  return {
    ...(ref ? { ref } : {}),
    title: latest.title,
    ...(composer ? { composer } : {}),
    opens: count(a.opens, b.opens),
    playSeconds: count(a.playSeconds, b.playSeconds),
    ...(lastOpened ? { lastOpened } : {}),
    ...(lastPlayed ? { lastPlayed } : {}),
  };
}

/**
 * This device's history with its own row in the account folded back in,
 * taking the larger of each count: when another account used the device, its
 * history was cleared from the device but not from your row, and on coming
 * back you have it again. Where the device already holds the row's plays the
 * larger count is the same count, never twice it.
 */
export function restoreHistory(local: TuneHistory, own: TuneHistory): TuneHistory {
  const out: TuneHistory = { ...local };
  for (const [key, entry] of Object.entries(own)) {
    const had = out[key];
    if (!had) {
      out[key] = { ...entry };
      continue;
    }
    const latest = lastActive(entry) > lastActive(had) ? entry : had;
    out[key] = combined(latest, had, entry, Math.max);
  }
  return prune(out);
}

/**
 * Recent tunes across your devices, newest first: this device's recents with
 * the tunes your other devices opened, in the order they were opened, each
 * once. This device's recents are timed by its own history; one opened
 * before the history was kept sits below everything timed, in its order.
 */
export function recentsAcrossDevices(
  recents: readonly PlaylistTuneRef[],
  history: TuneHistory,
  other: TuneHistory,
  limit = RECENT_LIMIT,
): PlaylistTuneRef[] {
  const opened = new Map<string, number>();
  for (const entry of Object.values(history)) {
    if (!entry.ref || !entry.lastOpened) continue;
    const key = playlistRefKey(entry.ref);
    opened.set(key, Math.max(opened.get(key) ?? 0, entry.lastOpened));
  }
  const best = new Map<string, { ref: PlaylistTuneRef; at: number; order: number }>();
  const offer = (ref: PlaylistTuneRef, at: number, order: number) => {
    const key = playlistRefKey(ref);
    const had = best.get(key);
    if (!had || at > had.at)
      best.set(key, { ref, at, order: had ? Math.min(had.order, order) : order });
  };
  recents.forEach((ref, i) => offer(ref, opened.get(playlistRefKey(ref)) ?? -1 - i, i));
  let order = recents.length;
  for (const entry of Object.values(other)) {
    if (entry.ref && entry.lastOpened) offer(entry.ref, entry.lastOpened, order++);
  }
  return [...best.values()]
    .sort((a, b) => b.at - a.at || a.order - b.order)
    .slice(0, limit)
    .map(({ ref }) => ref);
}

/** The tunes you have played most, longest first: each played at least
 * PLAYED_SECONDS and openable again (it has a ref). */
export function mostPlayed(history: TuneHistory, limit: number): TuneHistoryEntry[] {
  return Object.values(history)
    .filter((entry) => entry.ref && entry.playSeconds >= PLAYED_SECONDS)
    .sort(
      (a, b) =>
        b.playSeconds - a.playSeconds ||
        (b.lastPlayed ?? 0) - (a.lastPlayed ?? 0) ||
        a.title.localeCompare(b.title),
    )
    .slice(0, limit);
}

/** The home page's Most played shelf: empty until it would rank three tunes. */
export function mostPlayedShelf(history: TuneHistory): TuneHistoryEntry[] {
  const shelf = mostPlayed(history, MOST_PLAYED);
  return shelf.length >= MOST_PLAYED_MIN ? shelf : [];
}

/** How long, the way a card says it: "4 min", "1 h 5 min". */
export function playedLabel(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${Math.max(1, minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}
